import React from 'react';
import { useAdSummaryMetrics } from '../hooks/useAdSummaryMetrics';
import { getAdjustedDataSetsForSpline, getSplineTotals } from 'src/utils/chartUtils';
import MetricSummaryTrendCard from 'src/components/BeaconV2Components/MetricSummaryTrendCard/MetricSummaryTrendCard';
import { calculatePercentChange, safeDivide, updateUrlQueryParams } from 'src/utils/app';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { getDirection } from 'src/components/BeaconV2Components/utils/chartStyles';
import { useHistory, useMetricFormatter, useQueryParamValue } from 'src/utils/Hooks';
import { CommonSummaryInfoSubtitle } from 'src/components/EntityPage/CommonSummaryInfo/CommonSummaryInfo';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import MetricSummaryTrendCardLoading from 'src/components/BeaconV2Components/MetricSummaryTrendCard/MetricSummaryTrendCardLoading';
import { paidSearchMetrics } from './constants';

/**
 * Shows paid search key metric cards for topics
 */
export function PaidSearchTopicsKeyMetricCards() {
  const formatMetric = useMetricFormatter();
  const { getComparisonSplineData, getPrimarySplineData, isLoading } = useAdSummaryMetrics();
  const selectedMetric = useQueryParamValue('adMetric', 'adClicks');
  const history = useHistory();

  /**
   * Map each metric type to all the data required for displaying a spline data card
   */
  const splineDataMap = paidSearchMetrics.reduce((acc, { fieldId, func }) => {
    const rawPrimaryData = getPrimarySplineData(fieldId);
    const rawComparisonData = getComparisonSplineData(fieldId);
    const { primaryData, secondaryData } = getAdjustedDataSetsForSpline(rawPrimaryData, rawComparisonData);

    const { primaryTotal, secondaryTotal } = getSplineTotals(rawPrimaryData, rawComparisonData, {
      func: func || 'sum'
    });
    const percentChange = calculatePercentChange(primaryTotal, secondaryTotal);

    return {
      ...acc,
      [fieldId]: {
        primaryData,
        secondaryData,
        primaryTotal,
        secondaryTotal,
        percentChange
      }
    };
  }, {} as Record<string, { primaryData: number[][]; secondaryData: number[][]; primaryTotal: number; secondaryTotal: number; percentChange: number }>);

  const { primaryTotal: spendTotal, secondaryTotal: spendComparisonTotal } = splineDataMap.adSpend;
  const { primaryTotal: clicksTotal, secondaryTotal: clicksComparisonTotal } = splineDataMap.adClicks;
  const { primaryTotal: unitsSoldTotal, secondaryTotal: unitsSoldComparisonTotal } = splineDataMap.adUnitsSold;
  const { primaryTotal: impressionsTotal, secondaryTotal: impressionsComparisonTotal } = splineDataMap.adImpressions;
  const { primaryTotal: salesTotal, secondaryTotal: salesComparisonTotal } = splineDataMap.adSales;

  const totalsOverrides = {
    conversionRate: {
      primaryTotal: safeDivide(unitsSoldTotal, clicksTotal),
      secondaryTotal: safeDivide(unitsSoldComparisonTotal, clicksComparisonTotal)
    },
    cpc: {
      primaryTotal: safeDivide(spendTotal, clicksTotal),
      secondaryTotal: safeDivide(spendComparisonTotal, clicksComparisonTotal)
    },
    ctr: {
      primaryTotal: safeDivide(clicksTotal, impressionsTotal),
      secondaryTotal: safeDivide(clicksComparisonTotal, impressionsComparisonTotal)
    },
    cpm: {
      primaryTotal: safeDivide(spendTotal * 1000, impressionsTotal),
      secondaryTotal: safeDivide(spendComparisonTotal * 1000, impressionsComparisonTotal)
    },
    cpa: {
      primaryTotal: safeDivide(spendTotal, unitsSoldTotal),
      secondaryTotal: safeDivide(spendComparisonTotal, unitsSoldComparisonTotal)
    },
    roas: {
      primaryTotal: safeDivide(salesTotal, spendTotal),
      secondaryTotal: safeDivide(salesComparisonTotal, spendComparisonTotal)
    }
  };

  return (
    <Flex flexDirection="column" gap="lg">
      <CommonSummaryInfoSubtitle title="Paid Search" includeLegend />
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          columnGap: '24px',
          rowGap: '16px',
          transform: 'translateX(-16px)' // Aligns card text with page title
        }}
      >
        {paidSearchMetrics.map(({ fieldId, title, metricType }, index) => {
          // Only top 3 cards are large
          const variant = Math.floor(index / 3) === 0 ? 'large' : 'small';

          if (isLoading) {
            return <MetricSummaryTrendCardLoading key={fieldId} variant={variant} />;
          }

          const { percentChange, primaryData, primaryTotal, secondaryData } = splineDataMap[fieldId];

          const primaryTotalToUse = totalsOverrides[fieldId]?.primaryTotal ?? primaryTotal;
          const percentChangeToUse = totalsOverrides[fieldId]
            ? calculatePercentChange(totalsOverrides[fieldId].primaryTotal, totalsOverrides[fieldId].secondaryTotal)
            : percentChange;

          return (
            <MetricSummaryTrendCard
              key={fieldId}
              change={formatMetric(Math.abs(percentChangeToUse), METRICTYPE.PERCENT, { decimalPlaces: 2 })}
              direction={getDirection(percentChangeToUse)}
              title={title}
              value={formatMetric(primaryTotalToUse, metricType, {
                showFullValue: metricType === METRICTYPE.PERCENT,
                decimalPlaces: 2
              })}
              variant={variant}
              onClick={() => history.replace(updateUrlQueryParams({ adMetric: fieldId }))}
              selected={selectedMetric === fieldId}
              splinePreviewProps={{
                primaryData,
                secondaryData,
                metricType
              }}
            />
          );
        })}
      </div>
    </Flex>
  );
}
